import { useEffect, useSyncExternalStore } from "react";
import { nextApi, type TodayDto } from "../../api/nextApi";
import { EVENTS } from "../events";

/**
 * todayStore — shared state of "היום בתוכנה" (the button badge and the panel read the same payload).
 * One request per refresh; every 5 minutes, on data changes and when the tab regains focus.
 */
type State = { data: TodayDto | null; loading: boolean; error: string | null; loadedAt: number };
let state: State = { data: null, loading: false, error: null, loadedAt: 0 };
const listeners = new Set<() => void>();
const set = (patch: Partial<State>) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const snapshot = () => state;

let inflight: Promise<TodayDto | null> | null = null;
export function loadToday(force = false): Promise<TodayDto | null> {
  if (!force && state.data && Date.now() - state.loadedAt < 2 * 60 * 1000) return Promise.resolve(state.data);
  if (inflight) return inflight;
  set({ loading: true, error: null });
  inflight = nextApi
    .today()
    .then((data) => {
      set({ data, loading: false, loadedAt: Date.now() });
      return data;
    })
    .catch((e: unknown) => {
      set({ loading: false, error: (e as { message?: string })?.message || "לא הצלחנו לטעון את נתוני היום" });
      return null;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

let pollers = 0;
let stop: (() => void) | null = null;
export function useTodayState(enabled = true): State {
  const snap = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    if (!enabled) return undefined;
    pollers += 1;
    void loadToday();
    if (pollers === 1) {
      const t = window.setInterval(() => void loadToday(true), 5 * 60 * 1000);
      const onChanged = () => void loadToday(true);
      const onFocus = () => void loadToday();
      window.addEventListener(EVENTS.dataChanged, onChanged);
      window.addEventListener("focus", onFocus);
      stop = () => {
        window.clearInterval(t);
        window.removeEventListener(EVENTS.dataChanged, onChanged);
        window.removeEventListener("focus", onFocus);
      };
    }
    return () => {
      pollers = Math.max(0, pollers - 1);
      if (!pollers && stop) {
        stop();
        stop = null;
      }
    };
  }, [enabled]);
  return snap;
}

/* ── "טופל" per day, per user ── */
const DONE_KEY = (uid: string | null, day: string) => `tb-today-done:${uid || "anon"}:${day}`;
const DONE_EVENT = "tb:today-done";

export function readDone(uid: string | null, day: string): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(DONE_KEY(uid, day)) || "[]") as string[]);
  } catch {
    return new Set();
  }
}
export function writeDone(uid: string | null, day: string, done: Set<string>) {
  try {
    localStorage.setItem(DONE_KEY(uid, day), JSON.stringify(Array.from(done)));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(DONE_EVENT));
}
export function onDoneChange(cb: () => void) {
  window.addEventListener(DONE_EVENT, cb);
  return () => window.removeEventListener(DONE_EVENT, cb);
}

/* ── open by itself on the first visit of the day ── */
const AUTO_KEY = (uid: string | null) => `tb-today-auto:${uid || "anon"}`;
const SEEN_KEY = (uid: string | null) => `tb-today-seen:${uid || "anon"}`;
export function readAutoOpen(uid: string | null): boolean {
  try {
    return localStorage.getItem(AUTO_KEY(uid)) === "1";
  } catch {
    return false;
  }
}
export function writeAutoOpen(uid: string | null, on: boolean) {
  try {
    localStorage.setItem(AUTO_KEY(uid), on ? "1" : "0");
  } catch {
    /* ignore */
  }
}
/** true once per day (and marks the day as seen) */
export function takeFirstVisitToday(uid: string | null, day: string): boolean {
  try {
    if (localStorage.getItem(SEEN_KEY(uid)) === day) return false;
    localStorage.setItem(SEEN_KEY(uid), day);
    return true;
  } catch {
    return false;
  }
}

/** open decisions not marked "טופל" + my tasks for today */
export function todayBadge(data: TodayDto | null, done: Set<string>): number {
  if (!data) return 0;
  const attention = data.attention.filter((a) => !done.has(a.key)).length;
  return attention + (data.tasks?.mine.length ?? 0);
}
